import { AutocompleteProps } from '@zeiss/beyond-online-react';
import { Autocomplete, ReadOnlyField } from '@zeiss/lib-components';
import { useDebounce } from '@zeiss/lib-helpers';
import { TFunction } from 'i18next';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AutoCompleteLoading } from 'src/components/shared/AutoCompleteLoading';
import { DEBOUNCE_DELAY } from 'src/constants';
import { useSearchTenantsToInviteTo } from 'src/hooks/api';
import { SearchAndRedirectUrlContainer } from 'src/modules/Invitations/components/SearchAndRedirectUrlContainer';
import { GetTenantResponse } from 'src/types/rest/managementPortalApi';

interface TenantSearchProps {
  error?: AutocompleteProps['error'];
  onSelectTenant: (tenant: GetTenantResponse | null) => void;
  selectedLabel?: string;
  selectedRedirectUrl?: string;
}

interface GetSearchHelperTextProps {
  isPending: boolean;
  itemsCount: number;
  searchTerm: string;
  t: TFunction<['common']>;
}

const getHelperText = ({
  isPending,
  itemsCount,
  searchTerm,
  t,
}: GetSearchHelperTextProps) => {
  if (searchTerm.length === 0) {
    return '';
  }

  if (!isPending) {
    if (itemsCount > 0) {
      return t('common:input.searchHelperText');
    }

    if (itemsCount === 0) {
      return t('common:emptyState.searchNoX', {
        x: t('common:glossary.tenant'),
      });
    }
  }
};

export const TenantSearch = ({
  error,
  onSelectTenant,
  selectedLabel,
  selectedRedirectUrl,
}: TenantSearchProps) => {
  const { t } = useTranslation(['common', 'invitations']);

  /**
   * Track the input value separately from the search term to avoid the input value
   * being reset when the user selects an option from the autocomplete dropdown.
   * The input value is used to display the selected name in the input field,
   * while the search term is used to query the API for matching items.
   *
   * We could also use a single state and derive the other from it, but that would
   * require more complex logic and would be less clear.
   */
  const [searchTerm, setSearchTerm] = useState('');

  /**
   * `selectedLabel` seeds the initial input only. After mount this component owns
   * `inputValue` so typing and selection are not overwritten by later prop updates.
   */
  const [inputValue, setInputValue] = useState(selectedLabel ?? '');

  const [selectedTenant, setSelectedTenant] =
    useState<GetTenantResponse | null>(null);

  const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_DELAY);
  const { data, isPending } = useSearchTenantsToInviteTo(debouncedSearchTerm);

  const options = React.useMemo(() => {
    let result: AutocompleteProps['items'] = [];

    if (data) {
      result = data.tenants.map(({ name }) => name);
    }

    return result;
  }, [data]);

  const handleSelectionChange = (selectedKey: React.Key | null) => {
    if (typeof selectedKey !== 'string' || !data?.tenants) {
      return;
    }

    const selectedTenant = data.tenants.find(
      ({ name }) => name === selectedKey,
    );

    if (selectedTenant) {
      setSelectedTenant(selectedTenant);
      setSearchTerm('');
      setInputValue(selectedTenant.name);

      onSelectTenant(selectedTenant);
    }
  };

  const clearSelectedTenant = () => {
    setSelectedTenant(null);
    onSelectTenant(null);
  };

  const handleChange = (value: string) => {
    setSearchTerm(value);
    setInputValue(value);

    if (value === '') {
      clearSelectedTenant();
      return;
    }

    if (selectedTenant && value !== selectedTenant.name) {
      clearSelectedTenant();
    }
  };

  const handleBlur = () => {
    if (selectedTenant) {
      setSearchTerm('');
      setInputValue(selectedTenant.name);
    }
  };

  const loading = !!searchTerm && isPending;

  const helperText = getHelperText({
    isPending,
    itemsCount: options.length,
    searchTerm,
    t,
  });

  const redirectUrl = selectedTenant?.redirectUrl ?? selectedRedirectUrl;

  return (
    <SearchAndRedirectUrlContainer redirectUrl={redirectUrl}>
      <Autocomplete
        autoComplete="off"
        data-testid="TenantSearchAutoComplete"
        emptyState={loading ? <AutoCompleteLoading /> : undefined}
        error={error}
        helperText={helperText}
        inputValue={inputValue}
        items={options}
        label={t('common:glossary.tenant')}
        onBlur={handleBlur}
        onChange={handleChange}
        onSelectionChange={handleSelectionChange}
      />

      {redirectUrl ? (
        <ReadOnlyField
          text={redirectUrl}
          title={t('invitations:label.postInviteUrl')}
        />
      ) : null}
    </SearchAndRedirectUrlContainer>
  );
};
